import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck,
  ArrowRight,
  CheckCheck,
  Truck,
  RotateCcw,
  XCircle,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  ChevronUp,
  Store,
} from "lucide-react";
import { useCheckoutStore } from "../store/checkout-store";
import { useCheckoutApprovals } from "../hooks/use-checkout-approvals";
import { useCheckout } from "../hooks/use-checkout";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import type { MerchantApproval, SubmitApprovalItem } from "@/api/models/api-types";

interface MerchantCardProps {
  merchant: MerchantApproval;
  isApproved: boolean;
  alwaysApprove: boolean;
  onApproveChange: (merchantId: string, approved: boolean) => void;
  onAlwaysApproveChange: (merchantId: string, alwaysApprove: boolean) => void;
}

const MerchantCard: React.FC<MerchantCardProps> = ({
  merchant,
  isApproved,
  alwaysApprove,
  onApproveChange,
  onAlwaysApproveChange,
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [contactExpanded, setContactExpanded] = useState(false);

  // Auto-collapse card when approved, auto-expand when un-approved
  React.useEffect(() => {
    if (isApproved) setCollapsed(true);
    else setCollapsed(false);
  }, [isApproved]);

  const hasContent = (html: string) => html && html.trim().length > 0;

  const defaultTab = hasContent(merchant.cargo)
    ? "cargo"
    : hasContent(merchant.refund)
      ? "refund"
      : hasContent(merchant.cancellation)
        ? "cancellation"
        : "cargo";

  const renderHtml = (html: string) => {
    if (!hasContent(html)) {
      return (
        <p className="text-sm text-[#697586] dark:text-gray-400 italic py-3 text-center">
          {t("checkout.merchantApproval.noPolicyDefined")}
        </p>
      );
    }
    return (
      <div
        className="text-xs md:text-sm leading-relaxed text-[#535862] dark:text-gray-200
          [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mb-2 [&_h2]:mt-3 [&_h2]:first:mt-0
          [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:mb-1.5 [&_h3]:mt-2.5
          [&_p]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1.5
          [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1.5
          [&_li]:my-0.5
          [&_strong]:font-semibold
          [&_em]:italic [&_em]:text-[#697586] dark:[&_em]:text-gray-400
          [&_table]:w-full [&_table]:border-collapse [&_table]:my-2
          [&_td]:border [&_td]:border-[#e3e8ef] dark:[&_td]:border-gray-700 [&_td]:px-2.5 [&_td]:py-1.5 [&_td]:text-xs
          [&_th]:border [&_th]:border-[#e3e8ef] dark:[&_th]:border-gray-700 [&_th]:px-2.5 [&_th]:py-1.5
          [&_th]:bg-[#f9fafb] dark:[&_th]:bg-gray-800 [&_th]:font-semibold [&_th]:text-left [&_th]:text-xs
          [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  return (
    <div
      className={`rounded-2xl border-2 transition-all duration-200 ${
        isApproved
          ? "border-[#17b26a] bg-[#f0fdf4] dark:bg-green-950/20 dark:border-green-700 shadow-sm shadow-green-100 dark:shadow-none"
          : "border-[#e3e8ef] dark:border-gray-700 bg-white dark:bg-gray-900/40"
      }`}
    >
      {/* Merchant Header — tıklanınca collapse/expand */}
      <button
        type="button"
        onClick={() => setCollapsed((prev) => !prev)}
        className={`w-full flex items-center gap-3 px-5 py-3.5 rounded-t-[14px] transition-colors text-left ${
          collapsed ? "rounded-b-[14px]" : ""
        } ${
          isApproved
            ? "bg-[#dcfce7]/60 dark:bg-green-950/30 hover:bg-[#dcfce7]/80 dark:hover:bg-green-950/50"
            : "bg-[#fafafa] dark:bg-gray-800/40 hover:bg-[#f3f4f6] dark:hover:bg-gray-700/40"
        } ${
          !collapsed ? "border-b border-[#f1f2f4] dark:border-gray-700/60" : ""
        } ${
          isApproved && !collapsed ? "border-b border-[#bbf7d0] dark:border-green-800/60" : ""
        }`}
      >
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
            isApproved
              ? "bg-[#dcfce7] dark:bg-green-900/40"
              : "bg-[#f0f2f5] dark:bg-gray-700"
          }`}
        >
          <Store
            className={`w-4 h-4 ${
              isApproved ? "text-[#17b26a]" : "text-[#697586] dark:text-gray-400"
            }`}
          />
        </div>
        <span className="text-sm md:text-base font-semibold text-[#121926] dark:text-gray-100 flex-1">
          {merchant.merchantName}
        </span>
        {isApproved && (
          <div className="flex items-center gap-1.5 text-[#17b26a] text-xs font-medium mr-2">
            <ShieldCheck className="w-4 h-4" />
            <span className="hidden sm:inline">{t("checkout.merchantApproval.approved")}</span>
          </div>
        )}
        {collapsed ? (
          <ChevronDown className="w-4 h-4 text-[#9aa4b2] dark:text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronUp className="w-4 h-4 text-[#9aa4b2] dark:text-gray-500 flex-shrink-0" />
        )}
      </button>

      {/* Collapsible body */}
      {!collapsed && (
      <div className="p-4 md:p-5">
        <Tabs defaultValue={defaultTab}>
          <TabsList className="w-full grid grid-cols-3 mb-4 h-9">
            <TabsTrigger value="cargo" className="text-xs gap-1.5 font-medium">
              <Truck className="w-3.5 h-3.5" />
              {t("checkout.merchantApproval.cargo")}
            </TabsTrigger>
            <TabsTrigger value="refund" className="text-xs gap-1.5 font-medium">
              <RotateCcw className="w-3.5 h-3.5" />
              {t("checkout.merchantApproval.refund")}
            </TabsTrigger>
            <TabsTrigger value="cancellation" className="text-xs gap-1.5 font-medium">
              <XCircle className="w-3.5 h-3.5" />
              {t("checkout.merchantApproval.cancellation")}
            </TabsTrigger>
          </TabsList>

          {(["cargo", "refund", "cancellation"] as const).map((tab) => (
            <TabsContent key={tab} value={tab} className="mt-0">
              <div className="rounded-xl border border-[#e9eaeb] dark:border-gray-700 bg-[#fcfcfd] dark:bg-gray-800/60 max-h-[160px] overflow-y-auto px-4 py-3.5">
                {renderHtml(merchant[tab])}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Contact Info (Expandable) */}
        <button
          type="button"
          onClick={() => setContactExpanded((prev) => !prev)}
          className="w-full flex items-center justify-between text-xs font-medium text-[#697586] dark:text-gray-400 hover:text-[#364152] dark:hover:text-gray-200 transition-colors mt-3 py-1.5 border-t border-[#f1f2f4] dark:border-gray-700/60"
        >
          <span className="flex items-center gap-1.5 pt-1">
            <MapPin className="w-3.5 h-3.5" />
            {t("checkout.merchantApproval.contact")}
          </span>
          {contactExpanded ? (
            <ChevronUp className="w-3.5 h-3.5 mt-1" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 mt-1" />
          )}
        </button>

        {contactExpanded && (
          <div className="mt-2 rounded-xl border border-[#e9eaeb] dark:border-gray-700 bg-[#fafafa] dark:bg-gray-800/60 p-3.5 space-y-2">
            <p className="text-xs font-semibold text-[#364152] dark:text-gray-300">
              {merchant.contact.contactName}
            </p>
            <div className="flex items-center gap-2 text-xs text-[#697586] dark:text-gray-400">
              <Mail className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{merchant.contact.contactEmail}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#697586] dark:text-gray-400">
              <Phone className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{merchant.contact.contactPhone}</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-[#697586] dark:text-gray-400">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                {merchant.contact.address}, {merchant.contact.city},{" "}
                {merchant.contact.country}
              </span>
            </div>
          </div>
        )}

        {/* Approval Checkboxes */}
        <div className="mt-4 space-y-3 pt-4 border-t border-[#e9eaeb] dark:border-gray-700">
          <div className="flex items-start gap-3 p-3 rounded-xl bg-[#f8f9ff] dark:bg-indigo-950/20 border border-[#e8eaff] dark:border-indigo-900/30">
            <Checkbox
              id={`approve-${merchant.merchantId}`}
              checked={isApproved}
              onCheckedChange={(checked) =>
                onApproveChange(merchant.merchantId, checked as boolean)
              }
              className="mt-0.5 flex-shrink-0"
            />
            <Label
              htmlFor={`approve-${merchant.merchantId}`}
              className="text-xs md:text-sm font-medium text-[#364152] dark:text-gray-200 cursor-pointer leading-4 md:leading-5"
            >
              {t("checkout.merchantApproval.approveForThis")}
            </Label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id={`always-${merchant.merchantId}`}
              checked={alwaysApprove}
              onCheckedChange={(checked) =>
                onAlwaysApproveChange(merchant.merchantId, checked as boolean)
              }
              className="mt-0.5 flex-shrink-0"
              disabled={!isApproved}
            />
            <Label
              htmlFor={`always-${merchant.merchantId}`}
              className={`text-xs font-medium cursor-pointer leading-4 transition-colors ${
                isApproved
                  ? "text-[#697586] dark:text-gray-400"
                  : "text-[#c0c8d4] dark:text-gray-600"
              }`}
            >
              {t("checkout.merchantApproval.alwaysApprove")}
            </Label>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export const MerchantApprovalModal: React.FC = () => {
  const { t } = useTranslation();

  const {
    isApprovalModalOpen,
    approvalMerchants,
    pendingCheckoutPayload,
    closeApprovalModal,
  } = useCheckoutStore();

  const { submitApprovals, isSubmitting } = useCheckoutApprovals();
  const { executeCheckout, isProcessing } = useCheckout();

  const [approvedMap, setApprovedMap] = useState<Record<string, boolean>>({});
  const [alwaysApproveMap, setAlwaysApproveMap] = useState<Record<string, boolean>>({});

  React.useEffect(() => {
    if (isApprovalModalOpen) {
      setApprovedMap({});
      setAlwaysApproveMap({});
    }
  }, [isApprovalModalOpen]);

  const handleApproveChange = useCallback((merchantId: string, approved: boolean) => {
    setApprovedMap((prev) => ({ ...prev, [merchantId]: approved }));
    if (!approved) {
      setAlwaysApproveMap((prev) => ({ ...prev, [merchantId]: false }));
    }
  }, []);

  const handleAlwaysApproveChange = useCallback(
    (merchantId: string, always: boolean) => {
      setAlwaysApproveMap((prev) => ({ ...prev, [merchantId]: always }));
    },
    [],
  );

  const handleApproveAll = useCallback(() => {
    const allApproved: Record<string, boolean> = {};
    approvalMerchants.forEach((m) => {
      allApproved[m.merchantId] = true;
    });
    setApprovedMap(allApproved);
  }, [approvalMerchants]);

  const allApproved =
    approvalMerchants.length > 0 &&
    approvalMerchants.every((m) => approvedMap[m.merchantId]);

  const approvedCount = approvalMerchants.filter(
    (m) => approvedMap[m.merchantId],
  ).length;

  const progressPercent =
    approvalMerchants.length > 0
      ? Math.round((approvedCount / approvalMerchants.length) * 100)
      : 0;

  const handleContinue = async () => {
    if (!allApproved || !pendingCheckoutPayload) return;

    // Only send merchants where "always approve" is checked to the backend.
    // "Approve for this purchase only" merchants are allowed to proceed but not persisted.
    const permanentApprovals: SubmitApprovalItem[] = approvalMerchants
      .filter((m) => alwaysApproveMap[m.merchantId])
      .map((m) => ({ merchantId: m.merchantId, status: true }));

    if (permanentApprovals.length > 0) {
      try {
        await submitApprovals(permanentApprovals);
      } catch {
        // submitApprovals already shows a toast error; proceed anyway so user isn't blocked
      }
    }

    closeApprovalModal();
    await executeCheckout(pendingCheckoutPayload);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting && !isProcessing) {
      closeApprovalModal();
    }
  };

  return (
    <Dialog open={isApprovalModalOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        open={isApprovalModalOpen}
        className="max-w-xl md:max-w-2xl lg:max-w-3xl w-full p-0 gap-0 flex flex-col h-[90dvh] md:h-auto md:max-h-[88vh]"
      >
        {/* ── Header ─────────────────────────────── */}
        <DialogHeader className="px-6 pt-6 pb-5 border-b border-[#e3e8ef] dark:border-gray-700 flex-shrink-0">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-[#eef0ff] dark:bg-indigo-950/50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <ShieldCheck className="w-5 h-5 text-[#6172f3]" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base md:text-lg font-semibold text-[#121926] dark:text-gray-100">
                {t("checkout.merchantApproval.title")}
              </DialogTitle>
              <DialogDescription className="text-xs md:text-sm text-[#697586] dark:text-gray-400 mt-1 leading-5">
                {t("checkout.merchantApproval.subtitle")}
              </DialogDescription>
            </div>
          </div>

          {/* Progress bar + actions row */}
          <div className="mt-4 space-y-2.5">
            {/* Progress bar */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 rounded-full bg-[#e9eaeb] dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#17b26a] transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-xs font-medium text-[#697586] dark:text-gray-400 whitespace-nowrap tabular-nums">
                {approvedCount} / {approvalMerchants.length}{" "}
                {t("checkout.merchantApproval.approved")}
              </span>
            </div>

            {/* Approve All button */}
            {!allApproved && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleApproveAll}
                className="h-8 text-xs gap-1.5 border-[#d5d9eb] dark:border-gray-600 text-[#4a5568] dark:text-gray-300"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                {t("checkout.merchantApproval.approveAll")}
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* ── Merchant Cards ──────────────────────── */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">
            {approvalMerchants.map((merchant) => (
              <MerchantCard
                key={merchant.merchantId}
                merchant={merchant}
                isApproved={!!approvedMap[merchant.merchantId]}
                alwaysApprove={!!alwaysApproveMap[merchant.merchantId]}
                onApproveChange={handleApproveChange}
                onAlwaysApproveChange={handleAlwaysApproveChange}
              />
            ))}
          </div>
        </div>

        {/* ── Footer ─────────────────────────────── */}
        <div className="px-6 py-5 border-t border-[#e3e8ef] dark:border-gray-700 flex-shrink-0 space-y-3">
          {!allApproved && (
            <p className="text-xs text-center text-[#9aa4b2] dark:text-gray-500">
              {t("checkout.merchantApproval.approveAllHint")}
            </p>
          )}
          <Button
            onClick={handleContinue}
            disabled={!allApproved || isSubmitting || isProcessing}
            className="w-full py-3 text-sm font-semibold gap-2"
            variant="darkPrimary"
          >
            {isSubmitting || isProcessing ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" />
                {t("checkout.merchantApproval.continue")}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
